/**
 * Date Utilities
 *
 * All user-facing dates are displayed in South Africa Standard Time (UTC+2)
 * Backend dates remain in UTC for consistency
 */

// South Africa timezone
const TIMEZONE = "Africa/Johannesburg";

/**
 * Format a date for display in South Africa timezone (UTC+2)
 * @param date - Date string, Date object, or null/undefined
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string or fallback
 */
export function formatDateSAST(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }
): string {
  if (!date) return "—";

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "—";

    return dateObj.toLocaleString("en-ZA", {
      ...options,
      timeZone: TIMEZONE,
    });
  } catch {
    return "—";
  }
}

/**
 * Format a date with full details (date + time) in SAST
 */
export function formatDateTimeSAST(
  date: string | Date | null | undefined
): string {
  return formatDateSAST(date, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a date only (no time) in SAST
 */
export function formatDateOnlySAST(
  date: string | Date | null | undefined
): string {
  return formatDateSAST(date, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a time only (no date) in SAST
 */
export function formatTimeSAST(date: string | Date | null | undefined): string {
  return formatDateSAST(date, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a date in a more human-readable format for SAST
 * Example: "Dec 15, 2025, 1:26 PM"
 */
export function formatDatePretty(
  date: string | Date | null | undefined
): string {
  if (!date) return "—";

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "—";

    return dateObj.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: TIMEZONE,
    });
  } catch {
    return "—";
  }
}

/**
 * Get relative time string (e.g., "2 hours ago") - uses browser's locale
 * Note: This is approximate and doesn't account for timezone differences
 */
export function formatRelativeTime(
  date: string | Date | null | undefined
): string {
  if (!date) return "—";

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "—";

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "just now";
    if (diffMins < 60)
      return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;

    return formatDatePretty(date);
  } catch {
    return "—";
  }
}
